import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@snapchat/mw-common';

export const sessionIdStorageKey = 'mwp-ai-agent-session-id';
export const maxSessionIdChars = 512;

/**
 * Reads the session id from session storage. Returns undefined when no value
 * is stored, or when the stored value fails length validation (in which case
 * the corrupt value is also cleared from storage so subsequent reads don't
 * see it).
 *
 * @returns The stored session id, or undefined if absent or invalid.
 */
export const loadSessionId = (): string | undefined => {
  const raw = getSessionStorageItem(sessionIdStorageKey);
  if (!raw) return undefined;

  if (!isValidSessionId(raw)) {
    removeSessionStorageItem(sessionIdStorageKey);
    return undefined;
  }

  return raw;
};

/**
 * Writes the session id to session storage, provided it passes validation.
 *
 * Invalid ids are silently skipped rather than throwing, so callers can treat this as
 * fire-and-forget. Callers that need to know whether the id was accepted should check
 * {@link isValidSessionId} first.
 *
 * Browser errors from the underlying write (e.g. `QuotaExceededError`) are swallowed
 * by {@link setSessionStorageItem}, which logs a warning. The in-memory session id
 * keeps working; only the persisted copy is lost.
 *
 * @param sessionId - The session id to save.
 */
export const saveSessionId = (sessionId: string): void => {
  if (!isValidSessionId(sessionId)) return;

  setSessionStorageItem(sessionIdStorageKey, sessionId);
};

/**
 * Removes the session id from session storage.
 */
export const clearSessionId = (): void => {
  removeSessionStorageItem(sessionIdStorageKey);
};

/**
 * Validates a session id string. A session id is considered valid when it is
 * non-empty and does not exceed {@link maxSessionIdChars} characters.
 *
 * @param value - The session id to validate.
 * @returns True if the value is a valid session id, false otherwise.
 */
export const isValidSessionId = (value: string): boolean =>
  value.length > 0 && value.length <= maxSessionIdChars;
